import { resolveGroupLifeMonthlyPremium } from '@/features/quotes/utils/group-life/monthly-premium';
import type { GroupLifeQuoteCalculation } from '@/services/group-life';
import { roundHalfUp } from '@/shared/utils/round-half-up.util';

const MONTHS_IN_YEAR = 12;

/**
 * Linha MENSAL do demonstrativo por cobertura. Mesmo formato do
 * `calculation.breakdown` (`description` = código ou nome da cobertura).
 */
export type MonthlyCoveragePremium = {
  description: string;
  total: number;
  iof: number;
};

/**
 * Demonstrativo MENSAL por cobertura — o `breakdown` ANUAL do cálculo ÷ 12, com
 * as linhas fechando no prêmio mensal oficial (`calculation.monthly`).
 *
 * O Demonstrativo da proposta exibia o anual sem dividir (DH R$ 1.000.314,55 na
 * cotação 377, homologação 09-10) ao lado de um card de prêmio mensal. O valor
 * do `breakdown` em si confere com a soma vida a vida do motor
 * (`GroupPolicyCoverageData`) — só a base estava errada.
 *
 * O arredondamento por linha deixa centavos de diferença para o total oficial;
 * o resíduo (até 1 centavo por linha) vai para a maior linha. Diferença maior
 * não é escondida.
 */
export function resolveMonthlyCoverageBreakdown(
  calculation?: GroupLifeQuoteCalculation | null
): MonthlyCoveragePremium[] {
  const rows = (calculation?.breakdown ?? []).map(row => ({
    description: row.description,
    total: roundHalfUp(row.total / MONTHS_IN_YEAR),
    iof: roundHalfUp(row.iof / MONTHS_IN_YEAR),
  }));

  const monthly = resolveGroupLifeMonthlyPremium(calculation);
  if (monthly) {
    absorbResidual(rows, 'total', monthly.total);
    absorbResidual(rows, 'iof', monthly.iof);
  }
  return rows;
}

function absorbResidual(
  rows: MonthlyCoveragePremium[],
  key: 'total' | 'iof',
  target: number
) {
  if (rows.length === 0) return;
  const sum = rows.reduce((acc, row) => acc + row[key], 0);
  const residual = roundHalfUp(target - sum);
  if (residual === 0 || Math.abs(residual) > 0.01 * rows.length) return;
  const largest = rows.reduce((max, row) => (row.total > max.total ? row : max));
  largest[key] = roundHalfUp(largest[key] + residual);
}
